import { VisualShape } from 'app/frontend/types/custom/VisualPlan'
import { MAX_ENTITY_COUNT } from '../constants'
import { ClosedShape, getEntityLayout, isClosedShape } from './entityPositions'

const MAX_MARKER_RADIUS = 32
const MIN_MARKER_RADIUS = 4
const MAX_EDGE_GAP = 12
const EDGE_GAP_RATIO = 0.12
const MARKER_SIZE_RATIO = 0.5

export type EntityMarkerPlacement = {
  centerX: number
  centerY: number
  rotation: number
  markerRadius: number
  fontSize: number
  positions: { x: number; y: number }[]
}

const minPositionSeparation = (
  positions: { x: number; y: number }[],
): number | undefined => {
  if (positions.length < 2) return undefined
  let minDist = Infinity
  for (let i = 0; i < positions.length; i++) {
    for (let j = i + 1; j < positions.length; j++) {
      const dx = positions[i].x - positions[j].x
      const dy = positions[i].y - positions[j].y
      minDist = Math.min(minDist, Math.hypot(dx, dy))
    }
  }
  return minDist
}

export const getEntityMarkerPlacement = (
  shape: ClosedShape,
  width: number,
  height: number,
  maxMarkerRadius?: number,
): EntityMarkerPlacement => {
  const count = Math.min(shape.entity_count ?? 0, MAX_ENTITY_COUNT)
  const layout = getEntityLayout(shape, width, height, count)
  const separation = minPositionSeparation(layout.positions)
  const edgeGap = Math.min(
    MAX_EDGE_GAP,
    Math.min(layout.rx, layout.ry) * EDGE_GAP_RATIO,
  )

  const maxCenterX = layout.positions.reduce(
    (max, pos) => Math.max(max, Math.abs(pos.x)),
    0,
  )
  const maxCenterY = layout.positions.reduce(
    (max, pos) => Math.max(max, Math.abs(pos.y)),
    0,
  )

  const computeFit = (r: number) => {
    const ax = Math.max(0, layout.rx - r - edgeGap)
    const ay = Math.max(0, layout.ry - r - edgeGap)
    return Math.min(
      1,
      maxCenterX > 0 ? ax / maxCenterX : 1,
      maxCenterY > 0 ? ay / maxCenterY : 1,
    )
  }

  const shapeMin = Math.min(layout.rx, layout.ry)
  const effectiveMax = Math.min(MAX_MARKER_RADIUS, shapeMin * MARKER_SIZE_RATIO)

  let markerRadius: number
  let fit: number
  if (!separation) {
    markerRadius = Math.min(effectiveMax, shapeMin * 0.35)
    fit = computeFit(markerRadius)
  } else {
    markerRadius = effectiveMax
    fit = computeFit(markerRadius)
    for (let i = 0; i < 5; i++) {
      markerRadius = Math.min(
        effectiveMax,
        Math.max(MIN_MARKER_RADIUS, separation * fit * 0.45),
      )
      fit = computeFit(markerRadius)
    }
  }

  if (maxMarkerRadius !== undefined && markerRadius > maxMarkerRadius) {
    markerRadius = maxMarkerRadius
    fit = computeFit(markerRadius)
  }

  return {
    centerX: layout.centerX,
    centerY: layout.centerY,
    rotation: layout.rotation,
    markerRadius,
    fontSize: Math.max(8, Math.round(markerRadius * 0.95)),
    positions: layout.positions.map((pos) => ({
      x: pos.x * fit,
      y: pos.y * fit,
    })),
  }
}

export const getSharedMarkerRadius = (
  shapes: VisualShape[],
  width: number,
  height: number,
): number | undefined => {
  const radii = shapes
    .filter(
      (shape): shape is ClosedShape =>
        isClosedShape(shape) && (shape.entity_count ?? 0) > 0,
    )
    .map((shape) => getEntityMarkerPlacement(shape, width, height).markerRadius)
  return radii.length > 1 ? Math.min(...radii) : undefined
}
